<!-- Toolbar in two fixed rows, so switching views never reflows it:
     1) date, search, view toggle   2) status filter, then actions on the right.
     Waitlist-only mode has one view and no filter, so it is a single row. -->
<div class="rsv-toolbar">
  <div class="rsv-toolbar-row">
    <div class="rsv-date" data-tour="date">
      <button
        type="button"
        class="rsv-icon-btn"
        (click)="shiftDate(-1)"
        [disabled]="selectedDate <= minDate"
        aria-label="Previous day"
      >
        <i class="bx bx-chevron-left" aria-hidden="true"></i>
      </button>
      <input
        type="date"
        class="rsv-date-input"
        appPickerOnlyDate
        [min]="minDate"
        [max]="maxDate"
        [(ngModel)]="selectedDate"
        (change)="onDateChange()"
        aria-label="Service date"
      />
      <button
        type="button"
        class="rsv-icon-btn"
        (click)="shiftDate(1)"
        [disabled]="selectedDate >= maxDate"
        aria-label="Next day"
      >
        <i class="bx bx-chevron-right" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        class="rsv-today"
        [class.rsv-today--active]="isToday"
        (click)="goToday()"
      >
        Today
      </button>
    </div>

    <div class="rsv-search" data-tour="search">
      <i class="bx bx-search" aria-hidden="true"></i>
      <input
        type="text"
        placeholder="Search guests, phone, table…"
        aria-label="Search reservations"
        [(ngModel)]="searchQuery"
        (input)="onSearch()"
      />
    </div>

    <!-- A single view (waitlist-only mode) needs no switcher. -->
    @if (showViewTabs) {
      <div class="fnf-segmented fnf-segmented--compact" role="tablist" data-tour="views">
        <!-- Only the views the restaurant's mode runs: timed bookings and/or a queue. -->
        @if (isViewEnabled('book')) {
          <button
            type="button"
            class="fnf-segment"
            [class.fnf-segment--active]="view === 'book'"
            (click)="changeView('book')"
          >
            <i class="bx bx-book-open" aria-hidden="true"></i> Book
          </button>
          <button
            type="button"
            class="fnf-segment"
            [class.fnf-segment--active]="view === 'timeline'"
            (click)="changeView('timeline')"
          >
            <i class="bx bx-time-five" aria-hidden="true"></i> Timeline
          </button>
        }
        <!-- Pending requests across every date (FNF-308). -->
        @if (isViewEnabled('requests')) {
          <button
            type="button"
            class="fnf-segment"
            [class.fnf-segment--active]="view === 'requests'"
            (click)="changeView('requests')"
            data-testid="requests-tab"
          >
            <i class="bx bx-message-square-check" aria-hidden="true"></i> Requests
            @if (pendingRequestCount) {
              <span class="rsv-badge rsv-badge--warn">{{
                pendingRequestCount > 99 ? '99+' : pendingRequestCount
              }}</span>
            }
          </button>
        }
        @if (isViewEnabled('waitlist')) {
          <button
            type="button"
            class="fnf-segment"
            [class.fnf-segment--active]="view === 'waitlist'"
            (click)="changeView('waitlist')"
          >
            <i class="bx bx-hourglass" aria-hidden="true"></i> Waitlist
            @if (stats.waiting) {
              <span class="rsv-badge">{{ stats.waiting }}</span>
            }
          </button>
        }
      </div>
    }

    @if (queueOnly) {
      <ng-container [ngTemplateOutlet]="toolbarActions" />
    }
  </div>

  <!-- Waitlist-only has no status filter or add button, so its two icons
       join row 1 instead of sitting alone on a row of their own. -->
  @if (!queueOnly) {
    <div class="rsv-toolbar-row">
      @if (view !== 'waitlist' && view !== 'requests') {
        <div class="rsv-status-filters" role="group" aria-label="Filter by status">
          @for (filter of statusFilters; track filter.value) {
            <button
              type="button"
              class="rsv-status-filter"
              [class.rsv-status-filter--active]="statusFilter === filter.value"
              (click)="setStatusFilter(filter.value)"
            >
              {{ filter.label }}
              @if (filter.value === 'pending' && stats.pending) {
                <span class="rsv-badge rsv-badge--warn">{{ stats.pending }}</span>
              }
            </button>
          }
        </div>
      }

      <ng-container [ngTemplateOutlet]="toolbarActions" />
    </div>
  }
</div>

<ng-template #toolbarActions>
  <div class="rsv-toolbar-actions">
    <button
      type="button"
      class="rsv-icon-btn"
      (click)="startTour()"
      matTooltip="How this page works"
      aria-label="Take the tour of the reservations page"
      data-tour="help"
    >
      <i class="bx bx-help-circle" aria-hidden="true"></i>
    </button>
    <a
      class="rsv-icon-btn"
      routerLink="/main/reservation-settings"
      matTooltip="Reservation settings"
      aria-label="Reservation settings"
      data-tour="settings"
    >
      <i class="bx bx-cog" aria-hidden="true"></i>
    </a>
    <!-- Walk-ins are added from the form on the Waitlist view itself. -->
    @if (timedEnabled) {
      <button type="button" class="rsv-add-btn" (click)="newReservation()" data-tour="add">
        <i class="bx bx-plus" aria-hidden="true"></i> New reservation
      </button>
    }
  </div>
</ng-template>

@if (settings && settings.mode === 'off') {
  <div class="rsv-mode-banner" role="status">
    <i class="bx bx-info-circle" aria-hidden="true"></i>
    Reservations are switched off for this restaurant.
    <a routerLink="/main/reservation-settings">Choose a reservation mode</a> to start taking
    bookings.
  </div>
}
@if (queueOnly) {
  <div class="rsv-mode-banner" role="status">
    <i class="bx bx-hourglass" aria-hidden="true"></i>
    This restaurant runs a walk-in queue, so there are no timed bookings.
    <a routerLink="/main/reservation-settings">Switch to timed or hybrid</a> to take bookings ahead
    of time.
  </div>
}
<!-- FNF-444: a book that failed to load must never look like an empty day. -->
@if (loadError && !displayLoading) {
  <div class="rsv-mode-banner rsv-outage-banner" role="alert" data-testid="book-outage-banner">
    <i class="bx bx-error-circle" aria-hidden="true"></i>
    <span>
      @if (dayShown) {
        <strong>Couldn't refresh the book.</strong>
        Showing it as it was at {{ loadedAtLabel }}; changes since then may be missing.
      } @else if (loadError === 'unavailable') {
        <strong>The reservation book can't be loaded right now.</strong>
        The reservation system isn't responding.
      } @else {
        <strong>The reservation book couldn't be loaded.</strong>
      }
      Bookings already taken are safe. Trying again automatically.
    </span>
    <button
      type="button"
      class="rsv-btn rsv-outage-retry"
      (click)="retryLoad()"
      [disabled]="refreshing"
    >
      <i class="bx bx-refresh" [class.bx-spin]="refreshing" aria-hidden="true"></i> Try again
    </button>
  </div>
}
<!-- The hours banners describe the loaded date, so wait for this one to load. -->
@if (dayShown && timedEnabled && hoursState === 'unset') {
  <div class="rsv-mode-banner" role="status">
    <i class="bx bx-time-five" aria-hidden="true"></i>
    No serving hours are set, so there are no times guests or staff can book.
    <a routerLink="/main/restaurant-profile">Set up serving hours</a> to open bookings.
  </div>
} @else if (dayShown && timedEnabled && hoursState === 'closed') {
  <div class="rsv-mode-banner" role="status">
    <i class="bx bx-calendar-x" aria-hidden="true"></i>
    Closed on {{ selectedWeekday }}s: your serving hours don't cover this day, so it has no bookable
    times.
    <a routerLink="/main/restaurant-profile">Edit serving hours</a>
  </div>
} @else if (dayShown && hoursState === 'closed_date') {
  <div class="rsv-mode-banner" role="status" data-testid="closed-date-banner">
    <i class="bx bx-calendar-x" aria-hidden="true"></i>
    Closed on {{ selectedDateLabel }}{{ dayNote ? ' (' + dayNote + ')' : '' }}: no new bookings or
    walk-ins. Bookings already made for this day stay as they are.
    <a routerLink="/main/reservation-settings">Closed dates</a>
  </div>
}

@if (displayLoading) {
  <div
    class="d-flex flex-column align-items-center justify-content-center common-loading-container"
  >
    <div class="w-25 text-center">
      <img class="common-loading" src="assets/menu_loading.gif" alt="common loading" />
      <p class="common-loading-text">Loading reservations, please wait...</p>
    </div>
  </div>
} @else if (loadError && !dayShown) {
  <!-- Nothing for this date has loaded: no stats or rows, which would read
       as a day with no bookings. The outage banner above says why. -->
  <div class="rsv-outage-empty" data-testid="book-unavailable">
    <i class="bx bx-book-alt" aria-hidden="true"></i>
    <p class="rsv-outage-empty-title">The book for {{ selectedDateLabel }} isn't available</p>
    <p class="rsv-outage-empty-text">
      This is not an empty day. Nothing has been cancelled; the bookings just can't be shown until
      the reservation system is back.
    </p>
  </div>
} @else {
  <!-- Day summary -->
  <div class="rsv-stats" data-tour="stats">
    <!-- Bookings, covers and requests exist only in modes with timed bookings. -->
    @if (isViewEnabled('book')) {
      <div class="rsv-stat">
        <span class="rsv-stat-value">{{ stats.bookings }}</span
        ><span class="rsv-stat-label">{{ stats.bookings === 1 ? 'Booking' : 'Bookings' }}</span>
      </div>
      <div class="rsv-stat">
        <span class="rsv-stat-value">{{ stats.covers }}</span
        ><span class="rsv-stat-label">{{ stats.covers === 1 ? 'Cover' : 'Covers' }}</span>
      </div>
      <div class="rsv-stat" [class.rsv-stat--warn]="stats.pending">
        <span class="rsv-stat-value">{{ stats.pending }}</span
        ><span class="rsv-stat-label">To confirm</span>
      </div>
    }
    <div class="rsv-stat">
      <span class="rsv-stat-value">{{ stats.seated }}</span
      ><span class="rsv-stat-label">Seated now</span>
    </div>
    @if (isViewEnabled('waitlist')) {
      <div class="rsv-stat">
        <span class="rsv-stat-value">{{ stats.waiting }}</span
        ><span class="rsv-stat-label">Waiting</span>
      </div>
    }
    <span class="rsv-stat-mode"
      >Mode: <strong>{{ settings?.mode | titlecase }}</strong></span
    >
  </div>

  <div class="rsv-body" [class.rsv-body--with-panel]="selected">
    <div class="rsv-main">
      <!-- ------------------------------------------------------------ book -->
      @if (view === 'book') {
        @if (hasResults) {
          @for (group of groups; track group.label) {
            @if (group.items.length) {
              <section class="rsv-card">
                <header class="rsv-card-head">
                  <h2 class="rsv-card-title">{{ group.label }}</h2>
                  <span class="rsv-count"
                    >{{ group.items.length }}
                    {{ group.items.length === 1 ? 'booking' : 'bookings' }} · {{ group.covers }}
                    {{ group.covers === 1 ? 'cover' : 'covers' }}</span
                  >
                </header>
                <div class="rsv-scroll">
                  <table class="rsv-table">
                    <thead>
                      <tr>
                        <th scope="col">Time</th>
                        <th scope="col">Guest</th>
                        <th scope="col">Party</th>
                        <th scope="col">Table</th>
                        <th scope="col">Via</th>
                        <th scope="col">Status</th>
                        <th scope="col" class="rsv-col-actions">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (r of group.items; track r.reservationId) {
                        <tr
                          [class.rsv-row--selected]="r.reservationId === selectedId"
                          [class.rsv-row--closed]="
                            ['declined', 'cancelled', 'no_show', 'expired', 'completed'].includes(
                              r.status
                            )
                          "
                          (click)="select(r)"
                        >
                          <td class="rsv-time">
                            {{ timeOf(r) }}<span class="rsv-time-end">– {{ endOf(r) }}</span>
                          </td>
                          <td>
                            <div class="rsv-guest">
                              <span class="rsv-guest-name"
                                >{{ r.guest.name }}
                                @if (visitBadgeOf(r); as badge) {
                                  <span
                                    class="rsv-visit"
                                    [ngClass]="'rsv-visit--' + badge.tone"
                                    [matTooltip]="badge.tooltip"
                                    >{{ badge.label }}</span
                                  >
                                }
                              </span>
                              <span class="rsv-guest-meta"
                                >{{ phoneOf(r.guest.phone) || r.guest.email || '—' }}
                                @if (r.occasion) {
                                  · {{ r.occasion }}
                                }
                              </span>
                              @if (allergyOf(r); as allergy) {
                                <span class="rsv-allergy" [matTooltip]="'Allergy: ' + allergy"
                                  ><i class="bx bx-error" aria-hidden="true"></i>
                                  {{ allergy }}</span
                                >
                              }
                            </div>
                          </td>
                          <td class="rsv-party">
                            <i class="bx bx-group" aria-hidden="true"></i> {{ r.partySize }}
                          </td>
                          <td>{{ tableLabelOf(r) || '—' }}</td>
                          <td>
                            <span class="rsv-channel" [matTooltip]="channelLabels[r.channel]"
                              ><i
                                class="bx"
                                [ngClass]="channelIcon(r.channel)"
                                aria-hidden="true"
                              ></i
                            ></span>
                          </td>
                          <td>
                            <span class="rsv-status" [ngClass]="'rsv-status--' + r.status">
                              <span class="rsv-status-dot" aria-hidden="true"></span
                              >{{ statusLabels[r.status] }}
                            </span>
                            @if (riskLevel(r) === 'high') {
                              <span
                                class="rsv-risk rsv-risk--high"
                                matTooltip="High no-show risk (sample score)"
                                >risk</span
                              >
                            }
                          </td>
                          <td class="rsv-col-actions" (click)="$event.stopPropagation()">
                            <div class="rsv-row-actions">
                              @if (can(r, 'confirmed')) {
                                <button
                                  type="button"
                                  class="rsv-mini rsv-mini--ok"
                                  (click)="confirm(r)"
                                  matTooltip="Confirm"
                                >
                                  <i class="bx bx-check" aria-hidden="true"></i>
                                </button>
                              }
                              @if (can(r, 'seated')) {
                                <button
                                  type="button"
                                  class="rsv-mini rsv-mini--primary"
                                  (click)="select(r); seat(r)"
                                  [disabled]="!!seatBlockReason(r)"
                                  [matTooltip]="
                                    canSeatEarly(r)
                                      ? 'Here early? Open the booking to seat now'
                                      : seatBlockReason(r) || 'Seat now'
                                  "
                                >
                                  <i class="bx bx-chair" aria-hidden="true"></i>
                                </button>
                              }
                              @if (can(r, 'completed')) {
                                <button
                                  type="button"
                                  class="rsv-mini"
                                  (click)="complete(r)"
                                  matTooltip="Close table"
                                >
                                  <i class="bx bx-check-double" aria-hidden="true"></i>
                                </button>
                              }
                              <div class="btn-group custom-dropdown1">
                                <button
                                  type="button"
                                  class="rsv-mini"
                                  data-bs-toggle="dropdown"
                                  data-bs-popper-config='{"strategy":"fixed"}'
                                  aria-expanded="false"
                                  aria-label="More actions"
                                >
                                  <i class="bx bx-dots-horizontal-rounded" aria-hidden="true"></i>
                                </button>
                                <ul class="dropdown-menu dropdown-menu-end">
                                  <li>
                                    <a class="dropdown-item type-option" (click)="edit(r)"
                                      ><i class="bx bx-edit-alt menu-icon" aria-hidden="true"></i>
                                      Edit</a
                                    >
                                  </li>
                                  @if (can(r, 'declined')) {
                                    <li>
                                      <a class="dropdown-item type-option" (click)="decline(r)"
                                        ><i class="bx bx-x-circle menu-icon" aria-hidden="true"></i>
                                        Decline request</a
                                      >
                                    </li>
                                  }
                                  @if (can(r, 'no_show')) {
                                    <li>
                                      <a
                                        class="dropdown-item type-option"
                                        [class.disabled]="!!noShowBlockReason(r)"
                                        [attr.aria-disabled]="!!noShowBlockReason(r)"
                                        (click)="!noShowBlockReason(r) && noShow(r)"
                                        ><i class="bx bx-user-x menu-icon" aria-hidden="true"></i>
                                        Mark no-show
                                        @if (noShowBlockReason(r); as why) {
                                          <span class="rsv-menu-hint">· {{ why }}</span>
                                        }
                                      </a>
                                    </li>
                                  }
                                  @if (can(r, 'cancelled')) {
                                    <li>
                                      <a
                                        class="dropdown-item type-option delete-dropdown-item"
                                        (click)="cancel(r)"
                                        ><i class="bx bx-trash menu-icon" aria-hidden="true"></i>
                                        Cancel</a
                                      >
                                    </li>
                                  }
                                </ul>
                              </div>
                            </div>
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              </section>
            }
          }
        } @else if (reservations.length) {
          <div
            class="d-flex flex-column align-items-center justify-content-center common-empty-container"
          >
            <p class="common-empty-header">No matching reservations</p>
            <p class="common-empty-sub-text">Try another status filter or search term.</p>
          </div>
        } @else {
          <div
            class="d-flex flex-column align-items-center justify-content-center common-empty-container"
          >
            <img
              src="assets/no-orders-yet.svg"
              alt="No reservations"
              class="common-empty-img mb-3"
            />
            <p class="common-empty-header">
              No reservations on {{ selectedDate | date: 'EEEE, d MMM' }}
            </p>
            <p class="common-empty-sub-text">
              Bookings from the Fynkle app, Google and the phone will appear here.
            </p>
          </div>
        }
      }

      <!-- -------------------------------------------------------- requests -->
      @if (view === 'requests') {
        <app-request-queue
          [timeZone]="timeZone"
          (accept)="confirm($event)"
          (decline)="decline($event)"
          (open)="openRequest($event)"
        />
      }

      <!-- -------------------------------------------------------- timeline -->
      @if (view === 'timeline') {
        <section class="rsv-card">
          <header class="rsv-card-head">
            <h2 class="rsv-card-title">Service timeline</h2>
            <span class="rsv-count"
              >Pacing cap {{ settings?.pacing?.maxCoversPerInterval }} covers per
              {{ settings?.slotIntervalMinutes }} min</span
            >
          </header>
          <div class="rsv-timeline">
            @for (slot of timeline; track slot.start) {
              <div class="rsv-slot" [class.rsv-slot--over]="slot.pacingPct > 100">
                <div class="rsv-slot-time">{{ slot.label }}</div>
                <div class="rsv-slot-body">
                  <div class="rsv-slot-chips">
                    @for (r of slot.items; track r.reservationId) {
                      <button
                        type="button"
                        class="rsv-chip"
                        [ngClass]="'rsv-chip--' + r.status"
                        [class.rsv-chip--selected]="r.reservationId === selectedId"
                        (click)="select(r)"
                      >
                        <span class="rsv-chip-party">{{ r.partySize }}</span>
                        {{ r.guest.name }}
                        @if (allergyOf(r); as allergy) {
                          <i
                            class="bx bx-error rsv-chip-allergy"
                            [matTooltip]="'Allergy: ' + allergy"
                            [attr.aria-label]="'Allergy: ' + allergy"
                          ></i>
                        }
                        @if (visitBadgeOf(r); as badge) {
                          <span
                            class="rsv-visit"
                            [ngClass]="'rsv-visit--' + badge.tone"
                            [matTooltip]="badge.tooltip"
                            >{{ badge.label }}</span
                          >
                        }
                        <span class="rsv-chip-table">{{ tableLabelOf(r) }}</span>
                      </button>
                    }
                    @if (!slot.items.length) {
                      <span class="rsv-slot-empty">—</span>
                    }
                  </div>
                  <div class="rsv-pacing" [matTooltip]="slot.covers + ' covers in this interval'">
                    <div
                      class="rsv-pacing-bar"
                      [style.width.%]="slot.pacingPct > 100 ? 100 : slot.pacingPct"
                    ></div>
                    <span class="rsv-pacing-label">{{ slot.covers }}</span>
                  </div>
                </div>
              </div>
            }
          </div>
        </section>
      }

      <!-- -------------------------------------------------------- waitlist -->
      @if (view === 'waitlist') {
        <section class="rsv-card">
          <header class="rsv-card-head">
            <!-- The count is the Waiting stat above; no second badge here. -->
            <h2 class="rsv-card-title">Walk-in waitlist</h2>
          </header>

          <!-- join_waitlist always adds to the restaurant's today, so the form
               only shows there; another date would hide the guest it added. -->
          @if (isToday) {
            <form class="rsv-walkin" (ngSubmit)="addWalkIn()" data-tour="walkin">
              <div class="rsv-field">
                <label class="rsv-field-label" for="walkInName">Guest name</label>
                <input
                  id="walkInName"
                  type="text"
                  class="rsv-input"
                  placeholder="e.g. Priya Natarajan"
                  [(ngModel)]="walkIn.name"
                  (blur)="walkInNameTouched = true"
                  name="walkInName"
                  autocomplete="off"
                  required
                  [class.rsv-input--error]="
                    walkInNameTouched && walkIn.name.trim() && !walkInNameValid
                  "
                />
                @if (walkInNameTouched && walkIn.name.trim() && !walkInNameValid) {
                  <p class="rsv-field-error">A name needs letters, not only numbers.</p>
                }
              </div>
              <div class="rsv-field">
                <label class="rsv-field-label" for="walkInPhone"
                  >Phone <span class="rsv-optional">optional, for the text</span></label
                >
                <input
                  id="walkInPhone"
                  type="tel"
                  class="rsv-input"
                  placeholder="(555) 123-4567"
                  [ngModel]="walkIn.phone"
                  (input)="onWalkInPhoneInput($event)"
                  (blur)="walkInPhoneTouched = true"
                  name="walkInPhone"
                  autocomplete="off"
                  [class.rsv-input--error]="walkInPhoneTouched && !walkInPhoneValid"
                />
                @if (walkInPhoneTouched && !walkInPhoneValid) {
                  <p class="rsv-field-error">Enter a phone number as (555) 123-4567.</p>
                }
              </div>
              <div class="rsv-field rsv-field--num">
                <label class="rsv-field-label" for="walkInParty">Party size</label>
                <input
                  id="walkInParty"
                  type="number"
                  class="rsv-input"
                  min="1"
                  [max]="maxWalkInParty"
                  [(ngModel)]="walkIn.partySize"
                  name="walkInParty"
                  [class.rsv-input--error]="!walkInPartyValid"
                />
                @if (!walkInPartyValid) {
                  <p class="rsv-field-error">From 1 to {{ maxWalkInParty }} guests.</p>
                }
              </div>
              <div class="rsv-field rsv-field--action">
                <!-- Keeps the button level with the inputs, under the labels' line. -->
                <span class="rsv-field-label" aria-hidden="true">&nbsp;</span>
                <button type="submit" class="rsv-add-btn" [disabled]="!canAddWalkIn">
                  <i class="bx bx-plus" aria-hidden="true"></i> Add walk-in
                </button>
              </div>
              <!-- The phone above is today's booking: seat that, don't queue twice. -->
              @if (walkInBooking; as booked) {
                <div class="rsv-walkin-note rsv-walkin-note--booked" role="alert">
                  <i class="bx bx-calendar-check rsv-walkin-note-icon" aria-hidden="true"></i>
                  <div class="rsv-walkin-note-body">
                    <span class="rsv-walkin-note-title">
                      Already booked
                      <span class="rsv-booked-tag">Today {{ timeOf(booked) }}</span>
                    </span>
                    <span class="rsv-guest-meta">
                      {{ booked.guest.name }} · party of {{ booked.partySize }} ·
                      {{ tableLabelOf(booked) || 'no table yet' }} ·
                      {{ statusLabels[booked.status] }}
                    </span>
                  </div>
                  <button type="button" class="rsv-btn" (click)="openBooking(booked)">
                    Open booking
                  </button>
                </div>
              }
              <!-- Nobody here has this phone, and it isn't today's booking either. -->
              @if (walkInFirstTime && !walkInBooking) {
                <div class="rsv-walkin-badge-row">
                  <span class="rsv-first-time" role="status" data-testid="walkin-first-time">
                    <i class="bx bx-user-plus" aria-hidden="true"></i> First-Time Customer
                  </span>
                </div>
              }
              <!-- Found by the phone above (FNF-442). -->
              @if (walkInReturning; as guest) {
                <div class="rsv-walkin-note" role="status">
                  <i class="bx bx-user-check rsv-walkin-note-icon" aria-hidden="true"></i>
                  <div class="rsv-walkin-note-body">
                    <span class="rsv-walkin-note-title">
                      Returning guest
                      @for (tag of guest.standingNotes.tags; track tag) {
                        <span class="rsv-tag">{{ tag }}</span>
                      }
                    </span>
                    <span class="rsv-guest-meta">{{ walkInSummary }}</span>
                    @if (guest.standingNotes.allergies) {
                      <span class="rsv-allergy"
                        ><i class="bx bx-error" aria-hidden="true"></i> Allergy:
                        {{ guest.standingNotes.allergies }}</span
                      >
                    }
                    @if (guest.standingNotes.preferences) {
                      <span class="rsv-guest-meta"
                        >Prefers: {{ guest.standingNotes.preferences }}</span
                      >
                    }
                  </div>
                </div>
              }
              <!-- SMS opt-in, worded as registered for the Reservations Campaign. -->
              <p class="rsv-consent">
                {{ smsConsent.text }}
                <a [href]="smsConsent.termsUrl" target="_blank" rel="noopener">Terms</a> ·
                <a [href]="smsConsent.privacyUrl" target="_blank" rel="noopener">Privacy</a>
              </p>
            </form>
          } @else {
            <div class="rsv-queue-closed" role="note">
              <i class="bx bx-info-circle" aria-hidden="true"></i>
              <span>Walk-ins join today's queue.</span>
              <button type="button" class="rsv-btn" (click)="goToday()">Go to today</button>
            </div>
          }

          @if (isToday && tables.length) {
            <!-- Which tables are free right now, so a host can see where a party fits. -->
            <div class="rsv-strip-head">
              <span class="rsv-field-label">Tables right now</span>
              @if (loadedAtLabel) {
                <span class="rsv-strip-updated">Updated {{ loadedAtLabel }}</span>
              }
              <button
                type="button"
                class="rsv-icon-btn rsv-strip-refresh"
                (click)="refreshTables()"
                [disabled]="refreshing"
                matTooltip="Refresh tables and queue"
                aria-label="Refresh tables and queue"
              >
                <i class="bx bx-refresh" [class.bx-spin]="refreshing" aria-hidden="true"></i>
              </button>
            </div>
            <div class="rsv-table-strip" role="list" aria-label="Tables right now">
              @for (s of tableStrip; track s.table.id) {
                <span
                  class="rsv-table-chip"
                  [ngClass]="'rsv-table-chip--' + s.state"
                  role="listitem"
                  [matTooltip]="s.note"
                >
                  <strong>{{ s.table.name }}</strong>
                  <span class="rsv-table-chip-seats">{{ s.table.seats }} seats</span>
                  <span class="rsv-table-chip-note">{{ s.note }}</span>
                </span>
              }
            </div>
          }

          @if (activeWaitlist.length) {
            <ol class="rsv-queue">
              @for (w of activeWaitlist; track w.reservationId) {
                <li
                  class="rsv-queue-item"
                  [class.rsv-queue-item--notified]="w.status === 'notified'"
                  [class.rsv-row--selected]="w.reservationId === selectedId"
                  (click)="select(w)"
                >
                  <span class="rsv-queue-pos">{{ w.position }}</span>
                  <div class="rsv-guest">
                    <span class="rsv-guest-name"
                      >{{ w.guest.name }}
                      <span class="rsv-party-inline"
                        ><i class="bx bx-group" aria-hidden="true"></i> {{ w.partySize }}</span
                      >
                      @if (visitBadgeOf(w); as badge) {
                        <span
                          class="rsv-visit"
                          [ngClass]="'rsv-visit--' + badge.tone"
                          [matTooltip]="badge.tooltip"
                          >{{ badge.label }}</span
                        >
                      }
                    </span>
                    <span class="rsv-guest-meta">
                      {{ phoneOf(w.guest.phone) || 'No phone · call by name' }} · waited
                      {{ waitedMinutes(w) }} min · quoted ~{{ w.quotedWaitMinutes }} min
                    </span>
                    @if (allergyOf(w); as allergy) {
                      <span class="rsv-allergy" [matTooltip]="'Allergy: ' + allergy"
                        ><i class="bx bx-error" aria-hidden="true"></i> {{ allergy }}</span
                      >
                    }
                  </div>
                  <span class="rsv-status" [ngClass]="'rsv-status--' + w.status">
                    <span class="rsv-status-dot" aria-hidden="true"></span>
                    {{ statusLabels[w.status] }}
                    @if (w.status === 'notified' && expiresInMinutes(w) !== null) {
                      · {{ expiresInMinutes(w) }} min left
                    }
                  </span>
                  <div class="rsv-row-actions" (click)="$event.stopPropagation()">
                    @if (can(w, 'notified')) {
                      <button
                        type="button"
                        class="rsv-mini rsv-mini--ok"
                        (click)="notify(w)"
                        [matTooltip]="notifyHint(w)"
                        [attr.aria-label]="notifyHint(w)"
                        [disabled]="queueClosedFor(w) || !w.guest.phone"
                      >
                        <i class="bx bx-bell" aria-hidden="true"></i>
                      </button>
                    }
                    <button
                      type="button"
                      class="rsv-mini rsv-mini--primary"
                      (click)="select(w); seat(w)"
                      [disabled]="queueClosedFor(w) || !suggestTable(w)"
                      [matTooltip]="seatHint(w)"
                      [attr.aria-label]="seatHint(w)"
                    >
                      <i class="bx bx-chair" aria-hidden="true"></i>
                    </button>
                    <button
                      type="button"
                      class="rsv-mini"
                      (click)="cancel(w)"
                      matTooltip="Remove from queue"
                    >
                      <i class="bx bx-x" aria-hidden="true"></i>
                    </button>
                  </div>
                </li>
              }
            </ol>
          } @else if (isToday || !closedWaitlist.length) {
            <div
              class="d-flex flex-column align-items-center justify-content-center common-empty-container"
            >
              @if (isToday) {
                <p class="common-empty-header">Nobody is waiting</p>
                <p class="common-empty-sub-text">
                  Add a walk-in above, or guests can join from the Fynkle app.
                </p>
              } @else {
                <p class="common-empty-header">No walk-ins on this day</p>
              }
            </div>
          }

          <!-- Parties that left the queue (seated, removed, expired), so a host can
               see what happened to them, and close a seated walk-in's table from
               its card. Folded on today; open by default on any other day. -->
          @if (closedWaitlist.length) {
            <div class="rsv-closed">
              <button
                type="button"
                class="rsv-closed-toggle"
                (click)="toggleClosed()"
                [attr.aria-expanded]="showClosed"
              >
                <i
                  class="bx"
                  [ngClass]="showClosed ? 'bx-chevron-down' : 'bx-chevron-right'"
                  aria-hidden="true"
                ></i>
                Left the queue
                <span class="rsv-closed-counts">· {{ closedSummary }}</span>
              </button>
              @if (showClosed) {
                <ol class="rsv-closed-list">
                  @for (w of closedWaitlist; track w.reservationId) {
                    <li
                      class="rsv-closed-item"
                      [class.rsv-row--selected]="w.reservationId === selectedId"
                      (click)="select(w)"
                    >
                      <span class="rsv-guest-name"
                        >{{ w.guest.name }}
                        <span class="rsv-party-inline"
                          ><i class="bx bx-group" aria-hidden="true"></i> {{ w.partySize }}</span
                        ></span
                      >
                      <span class="rsv-status" [ngClass]="'rsv-status--' + w.status">
                        <span class="rsv-status-dot" aria-hidden="true"></span>
                        {{ statusLabels[w.status] }}
                      </span>
                      <span class="rsv-guest-meta">{{ closedDetail(w) }}</span>
                    </li>
                  }
                </ol>
              }
            </div>
          }
        </section>
      }
    </div>

    <!-- ----------------------------------------------------- detail panel -->
    @if (selected; as r) {
      <aside class="rsv-panel" data-tour="panel">
        <header class="rsv-panel-head">
          <div>
            <h3 class="rsv-panel-title">{{ r.guest.name }}</h3>
            <span class="rsv-panel-sub">
              @if (r.kind === 'waitlist') {
                Walk-in · position {{ r.position ?? '—' }}
              } @else {
                {{ timeOf(r) }} – {{ endOf(r) }} · {{ r.serviceDate | date: 'EEE d MMM' }}
              }
            </span>
          </div>
          <button
            type="button"
            class="rsv-icon-btn"
            (click)="select(null)"
            aria-label="Close panel"
          >
            <i class="bx bx-x" aria-hidden="true"></i>
          </button>
        </header>

        <div class="rsv-panel-status">
          <span class="rsv-status" [ngClass]="'rsv-status--' + r.status"
            ><span class="rsv-status-dot" aria-hidden="true"></span
            >{{ statusLabels[r.status] }}</span
          >
          <span class="rsv-channel rsv-channel--label"
            ><i class="bx" [ngClass]="channelIcon(r.channel)" aria-hidden="true"></i>
            {{ channelLabels[r.channel] }}</span
          >
          @if (riskLevel(r); as level) {
            <span
              class="rsv-risk"
              [ngClass]="'rsv-risk--' + level"
              matTooltip="No-show risk from the sample model"
              >{{ level }} risk · {{ (r.risk?.noShowScore ?? 0) * 100 | number: '1.0-0' }}%</span
            >
          }
          @if (visitBadgeOf(r); as badge) {
            <span
              class="rsv-visit"
              [ngClass]="'rsv-visit--' + badge.tone"
              [matTooltip]="badge.tooltip"
              >{{ badge.label }}</span
            >
          }
        </div>

        @if (allergyOf(r); as allergy) {
          <p class="rsv-allergy-banner" role="note">
            <i class="bx bx-error" aria-hidden="true"></i>
            <span><strong>Allergy:</strong> {{ allergy }}</span>
          </p>
        }

        <dl class="rsv-panel-rows">
          <div>
            <dt>Party</dt>
            <dd>{{ r.partySize }} guests</dd>
          </div>
          <div>
            <dt>Table</dt>
            <dd>{{ tableLabelOf(r) || 'Not assigned' }}</dd>
          </div>
          <div>
            <dt>Phone</dt>
            <dd>{{ phoneOf(r.guest.phone) || '—' }}</dd>
          </div>
          <div>
            <dt>Email</dt>
            <dd>{{ r.guest.email || '—' }}</dd>
          </div>
          <div>
            <dt>Visits</dt>
            <dd>
              {{ r.guest.visits ?? 0 }}
              @if (lastVisitOf(r); as last) {
                <span class="rsv-hint">· last {{ last }}</span>
              }
            </dd>
          </div>
          <!-- Neutral, recent bookings only, and absent when there are none
               (FNF-1187). Never shown in the lists. -->
          @if (noShowLineOf(r); as missed) {
            <div>
              <dt>History</dt>
              <dd class="rsv-muted">{{ missed }}</dd>
            </div>
          }
          @if (r.occasion) {
            <div>
              <dt>Occasion</dt>
              <dd>{{ r.occasion }}</dd>
            </div>
          }
          @if (r.kind === 'waitlist') {
            <div>
              <dt>Quoted</dt>
              <dd>~{{ r.quotedWaitMinutes }} min · waited {{ waitedMinutes(r) }}</dd>
            </div>
          }
          <!-- A request is declined automatically at its deadline (FNF-308). -->
          @if (requestDeadlineOf(r); as deadline) {
            <div class="rsv-panel-wide" data-testid="request-deadline">
              <dt>Answer by</dt>
              <dd>
                {{ stampOf(r.expiresAt!) }} <span class="rsv-hint">· {{ deadline }}</span>
              </dd>
            </div>
          }
          @if (declineReasonOf(r); as why) {
            <div class="rsv-panel-wide" data-testid="decline-reason">
              <dt>Reason</dt>
              <dd>{{ why }}</dd>
            </div>
          }
        </dl>

        <!-- Kept on the guest, so they are on every booking without retyping
             (FNF-442). The booking's own note stays below, apart. -->
        <section class="rsv-standing" aria-label="Guest notes">
          <div class="rsv-standing-head">
            <span class="rsv-label">Guest notes · every visit</span>
            @if (r.guest.standingNotes && !notesEditing) {
              <button type="button" class="rsv-link" (click)="startNotes(r)">
                {{ hasNotes(r) ? 'Edit' : 'Add' }}
              </button>
            }
          </div>
          @if (!historySupported(r)) {
            <!-- An older reservation-service: it neither sends nor keeps them. -->
            <p class="rsv-hint">
              Guest notes and visit history aren't available from the server yet.
            </p>
          } @else {
            @if (r.guest.standingNotes; as notes) {
              @if (notesEditing) {
                <div class="rsv-standing-form">
                  <label class="rsv-label" for="gnAllergies">Allergies</label>
                  <input
                    id="gnAllergies"
                    class="rsv-input"
                    maxlength="200"
                    placeholder="e.g. Tree nuts, shellfish"
                    [(ngModel)]="notesDraft.allergies"
                  />
                  <label class="rsv-label" for="gnPreferences">Preferences</label>
                  <input
                    id="gnPreferences"
                    class="rsv-input"
                    maxlength="300"
                    placeholder="e.g. Window table, high chair"
                    [(ngModel)]="notesDraft.preferences"
                  />
                  <label class="rsv-label" for="gnOccasions">Occasions</label>
                  <input
                    id="gnOccasions"
                    class="rsv-input"
                    maxlength="200"
                    placeholder="e.g. Birthday in June"
                    [(ngModel)]="notesDraft.occasions"
                  />
                  <label class="rsv-label" for="gnTags">Tags</label>
                  <input
                    id="gnTags"
                    class="rsv-input"
                    placeholder="e.g. regular, VIP"
                    [(ngModel)]="notesDraft.tags"
                  />
                  <p class="rsv-hint">Separate tags with commas. Clear a field to remove it.</p>
                  <div class="rsv-standing-actions">
                    <button
                      type="button"
                      class="rsv-btn rsv-btn--ok"
                      (click)="saveNotes(r)"
                      [disabled]="savingNotes"
                    >
                      <i class="bx bx-check" aria-hidden="true"></i> Save
                    </button>
                    <button type="button" class="rsv-btn" (click)="cancelNotes()">Cancel</button>
                  </div>
                </div>
              } @else if (hasNotes(r)) {
                <dl class="rsv-panel-rows rsv-standing-rows">
                  @if (notes.allergies) {
                    <div>
                      <dt>Allergies</dt>
                      <dd>{{ notes.allergies }}</dd>
                    </div>
                  }
                  @if (notes.preferences) {
                    <div>
                      <dt>Preferences</dt>
                      <dd>{{ notes.preferences }}</dd>
                    </div>
                  }
                  @if (notes.occasions) {
                    <div>
                      <dt>Occasions</dt>
                      <dd>{{ notes.occasions }}</dd>
                    </div>
                  }
                </dl>
                @if (notes.tags.length) {
                  <div class="rsv-tags">
                    @for (tag of notes.tags; track tag) {
                      <span class="rsv-tag">{{ tag }}</span>
                    }
                  </div>
                }
              } @else {
                <p class="rsv-hint">Nothing noted yet: allergies, a usual table, a birthday…</p>
              }
            } @else {
              <p class="rsv-hint">
                Add a phone number or an email to the booking to keep notes for this guest's next
                visits.
              </p>
            }
          }
        </section>

        @if (r.notes) {
          <span class="rsv-label">This booking</span>
          <p class="rsv-notes"><i class="bx bx-note" aria-hidden="true"></i> {{ r.notes }}</p>
        }

        @if (can(r, 'seated') && !queueClosedFor(r)) {
          <div class="rsv-seat">
            <label class="rsv-label" for="seatTable">Seat at</label>
            @if (!canSeatEarly(r) && seatBlockReason(r); as why) {
              <!-- Outside the seat window (and not today): no picker, just the
                   booked table and when. -->
              <p class="rsv-seat-later">
                {{ tableLabelOf(r) || 'No table yet' }}
                <span class="rsv-hint">· {{ why }}</span>
              </p>
            } @else {
              <div class="rsv-seat-row">
                <select id="seatTable" class="rsv-input" [(ngModel)]="seatTableId">
                  @for (t of freeTablesFor(r); track t.id) {
                    <option [value]="t.id">{{ t.name }} · {{ t.seats }} seats</option>
                  }
                  @if (!freeTablesFor(r).length) {
                    <option value="" disabled>No free table fits this party</option>
                  }
                </select>
                <button
                  type="button"
                  class="rsv-add-btn"
                  (click)="seat(r)"
                  [disabled]="!seatTableId"
                >
                  <i class="bx bx-chair" aria-hidden="true"></i>
                  {{
                    canSeatEarly(r)
                      ? 'Guest is here: seat now'
                      : r.status === 'no_show'
                        ? 'Guest arrived: seat'
                        : 'Seat'
                  }}
                </button>
              </div>
              @if (r.status === 'no_show') {
                <p class="rsv-hint">
                  Marked a no-show. Seating them corrects it, and it counts as a visit instead.
                </p>
              }
              @if (allergyOf(r); as allergy) {
                <p class="rsv-hint rsv-hint--allergy">
                  <i class="bx bx-error" aria-hidden="true"></i> Allergy: {{ allergy }}. Tell the
                  server.
                </p>
              }
              @if (canSeatEarly(r)) {
                <p class="rsv-hint">
                  Booked for {{ timeOf(r) }}. Seating now moves the booking to now; the guest isn't
                  sent a change notice.
                </p>
              }
              @if (bookedTableTaken(r)) {
                <p class="rsv-hint">{{ tableLabelOf(r) }} is taken right now.</p>
              }
            }
            <button type="button" class="rsv-link" (click)="openFloorPlan()">
              Open floor plan
            </button>
          </div>
        }

        <div class="rsv-panel-actions">
          @if (can(r, 'confirmed')) {
            <button type="button" class="rsv-btn rsv-btn--ok" (click)="confirm(r)">
              <i class="bx bx-check" aria-hidden="true"></i> Confirm
            </button>
          }
          @if (can(r, 'declined')) {
            <button type="button" class="rsv-btn" (click)="decline(r)">
              <i class="bx bx-x-circle" aria-hidden="true"></i> Decline
            </button>
          }
          @if (can(r, 'notified') && !queueClosedFor(r)) {
            <button
              type="button"
              class="rsv-btn rsv-btn--ok"
              (click)="notify(r)"
              [disabled]="!r.guest.phone"
              [matTooltip]="r.guest.phone ? '' : 'No phone number, so call by name'"
            >
              <i class="bx bx-bell" aria-hidden="true"></i> Text: table ready
            </button>
          }
          @if (r.status === 'notified') {
            <button type="button" class="rsv-btn" (click)="backToWaiting(r)">
              <i class="bx bx-undo" aria-hidden="true"></i> Back to waiting
            </button>
          }
          @if (can(r, 'completed')) {
            <button type="button" class="rsv-btn" (click)="complete(r)">
              <i class="bx bx-check-double" aria-hidden="true"></i> Close table
            </button>
          }
          @if (can(r, 'no_show')) {
            <button
              type="button"
              class="rsv-btn"
              (click)="noShow(r)"
              [disabled]="!!noShowBlockReason(r)"
              [matTooltip]="noShowBlockReason(r) || ''"
            >
              <i class="bx bx-user-x" aria-hidden="true"></i> No-show
            </button>
          }
          @if (
            r.kind === 'reservation' &&
            !['completed', 'no_show', 'cancelled', 'declined'].includes(r.status)
          ) {
            <button type="button" class="rsv-btn" (click)="edit(r)">
              <i class="bx bx-edit-alt" aria-hidden="true"></i> Edit
            </button>
          }
          @if (can(r, 'cancelled')) {
            <button type="button" class="rsv-btn rsv-btn--danger" (click)="cancel(r)">
              <i class="bx bx-trash" aria-hidden="true"></i>
              {{ r.kind === 'waitlist' ? 'Remove' : 'Cancel' }}
            </button>
          }
        </div>

        <p class="rsv-hint">
          Created {{ stampOf(r.createdAt) }} · updated {{ clockOf(r.modifiedAt) }} · v{{
            r.version
          }}
        </p>
      </aside>
    }
  </div>
}

@if (tourOpen) {
  <fnf-tour [steps]="tourSteps" (stepChange)="onTourStep($event)" (closed)="endTour($event)" />
}
